import { applicationContext, uuid, QueryUtility, toUTCDate, resolveInitialEligibilityStatus } from 'core/utils';
import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import { action, observable, computed, runInAction } from 'mobx';
import { FacilityOrderService, FacilityOrderStatusService } from "application/facility-order/services";
import { LookupService } from "common/services";
import _ from 'lodash';
import { NikoPatientDetailService, NikoPatientTemplateService } from "application/niko-patient/services";
import { ModalParams } from 'core/models';
import { HistoryService } from '../../history/services';
import { EligibilityStatusService } from 'application/eligibility-status/services';

@applicationContext
class NikoFacilityOrderInsuranceReviewPreviewViewStore extends BaseViewStore {
    @observable accessor facilityOrder;
    @observable accessor refreshKey = uuid();
    @observable accessor selectedStatusAbrv;
    @observable accessor fetchingData = false;
    @observable accessor disabled = false;
    @observable accessor nikoPatientDetails = [];
    @observable accessor insuranceChangeHistoryMap = {};
    @observable accessor showInsuranceChangeHistory = null;
    @observable accessor isReviewed = undefined;

    @computed
    get insuranceReviewStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "insurance-review") : undefined; }
    @computed
    get initialIntakeStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "initial-intake") : undefined; }
    @computed
    get usIntakeStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "us-intake") : undefined; }
    @computed
    get clinicalReviewStatus() { return this.facilityOrder ? this.facilityOrder.facilityOrderStatuses.find(x => x.status.abrv == "clinical-review") : undefined; }
    @computed
    get disabledByStatus() { return !(this.insuranceReviewStatus && this.insuranceReviewStatus.startedOn); }
    @computed
    get isSkipped() { return !this.insuranceReviewStatus && (!!this.usIntakeStatus || !!this.clinicalReviewStatus); }
    @computed
    get disabledByCompletion() { return !!(this.insuranceReviewStatus && this.insuranceReviewStatus.completedOn) || !!(this.usIntakeStatus && this.usIntakeStatus.startedOn); }
    @computed
    get isInitialIntakeCompleted() { return !!(this.initialIntakeStatus && this.initialIntakeStatus.completedOn); }
    @computed
    get disabledBySendOrder() { return !this.isAdmin && this.facilityOrder && this.facilityOrder.sendOrder; }

    tabItems = [{ abrv: 'in-progress', name: 'In Progress' }, { abrv: 'discharge', name: 'Discharges' }, { abrv: 'intake-reviewed', name: 'Insurance Reviewed' }];
    statuses = [{ id: "Hospital" }, { id: "Discharged" }, { id: "Expired" }, { id: "Active" }];

    constructor(rootStore) {
        super(rootStore);

        this.id = rootStore.routerStore.routerState.params.id;
        this.isAdmin = this.rootStore.userStore.user.roles.includes("Administrators");
        this.userId = this.rootStore.userStore.user.id;
        this.pageName = 'ir';

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        this.patientTemplateService = new NikoPatientTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.historyService = new HistoryService(rootStore.application.baasic.apiClient);
        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);

        const userRoles = this.rootStore.userStore.applicationUser.roles;
        this.isHistoryVisible = !(userRoles.includes("InitialIntake") || userRoles.includes("InsuranceReview"));

        this.nikoPatientInsuranceInfoNoteModal = new ModalParams({});
        this.nikoPatientStatusModal = new ModalParams({});
        this.nikoFacilityOrderReasonForChangeModal = new ModalParams({});

        this.statusStore = new BaasicDropdownStore(
            { multi: false, filterable: false, textField: "id" },
            { fetchFunc: () => this.statuses }
        );

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            await this.fetchFacilityOrderData(filter);
        }, {});

        this.fetch([
            this.initialize()
        ]);
    }

    async initialize() {
        await this.getFacilityOrder();
        await this.getLookups();
        await this.getData();
    }

    @action.bound
    async getFacilityOrder() {
        this.facilityOrder = (await this.facilityOrderService.get(this.id, { embed: 'Facility,FacilityOrderStatuses' })).data;
        this.rootStore.titleStore.setTitle(`Insurance Review - ${this.facilityOrder.facility.name}`);
    }

    @action.bound
    async getLookups() {
        this.lookups = await this.lookupService.getLookups("FacilityScheduleType,NewFlowStatus,ImportHeaderStatus");
        this.orderingTypeId = this.lookups.facilityScheduleType.find((s) => s.abrv === 'ordering').id;
        this.inProgressStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'in-progress').id;
        this.changeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'change').id;
        this.noChangeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'no-change').id;
        this.dischargeStatusId = this.lookups.newFlowStatus.find((s) => s.abrv === 'discharge').id;
        this.activeNewFlowStatusIds = `${this.inProgressStatusId},${this.changeStatusId},${this.noChangeStatusId}`;

        this.eligibilityStatuses = (await this.eligibilityStatusService.find({ rpp: 100 })).data.item;
    }

    @action.bound
    async getData() {
        this.page = "insurance-review";
        this.isInventoryStepAllowed = (await this.facilityOrderService.isNikoInventoryStepAllowed(this.id)).data;

        const selectedStatusAbrv = (this.insuranceReviewStatus && this.insuranceReviewStatus.completedOn && !(await this.hasAnyTemplateInProgress())) ? 'intake-reviewed' : 'in-progress';
        this.setStatus(selectedStatusAbrv);
    }

    @action.bound
    async hasAnyTemplateInProgress() {
        let params = {
            facilityOrderId: this.id,
            intakeStatusIds: this.activeNewFlowStatusIds,
            isInsuranceReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        return (await this.patientTemplateService.find(params)).data.item.some(x => x.id);
    }

    @action.bound
    setStatus(statusAbrv) {
        if (this.selectedStatusAbrv == statusAbrv || this.fetchingData) {
            return;
        }
        this.selectedStatusAbrv = statusAbrv;
        this.queryUtility.filter.set('intakeStatusIds', this.selectedStatusAbrv === "discharge" ? this.dischargeStatusId : this.activeNewFlowStatusIds);
        this.queryUtility.fetch();
    }

    @action.bound
    async fetchFacilityOrderData(filter) {
        this.fetchingData = true;
        let params = {
            ...filter,
            facilityOrderId: this.id,
            isInsuranceReviewed: this.selectedStatusAbrv == "in-progress" ? false : this.selectedStatusAbrv == "intake-reviewed" ? true : undefined,
            pageSize: 10000,
            embed: 'NikoPatientDetail,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory',
            orderBy: "SubCategory",
            orderDirection: "asc",
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;
        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: this.selectedStatusAbrv == "in-progress" && patientTemplates[0].nikoPatientDetail.status === 'Hospital',
            templatesVisible: false,
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            nikoPatientDetail.history = { userName: '', eventDate: null };
            this.setHasHistory(nikoPatientDetail);
            this.setDefaultTemplatesVisibility(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            this.getSelectedStatus("insurance", nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.isAddingNote = false;
                this.getSelectedStatus("intake", null, template);
            });
        });

        if (this.insuranceReviewStatus && !this.insuranceReviewStatus.completedOn && this.selectedStatusAbrv != "intake-reviewed") {
            this.checkIsReviewed(false);
        }

        this.nikoPatientDetails = nikoPatientDetails;
        this.fetchingData = false;
    }

    @action.bound
    async fetchPatientTemplateByPatient(nikoPatientDetailId) {
        if (this.insuranceReviewStatus && !this.insuranceReviewStatus.completedOn && this.selectedStatusAbrv != "intake-reviewed") {
            await this.checkIsReviewed(false);
        }

        let index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        let params = {
            intakeStatusIds: this.queryUtility.filter.intakeStatusIds,
            facilityOrderId: this.id,
            isInsuranceReviewed: this.selectedStatusAbrv == "in-progress" ? false : this.selectedStatusAbrv == "intake-reviewed" ? true : undefined,
            pageSize: 10000,
            embed: 'NikoPatientDetail,PatientTemplateItems,SubCategory,NikoPatientDetail.EligibilityStatus,NikoPatientDetail.NikoPatientDetailInsurances,OrderingDoctor,PatientTemplateDiagnosisCodes,PatientTemplateMissingDocuments,ClinicalStatus,ItemCategory',
            orderBy: "SubCategory",
            orderDirection: "asc",
            nikoPatientDetailId: nikoPatientDetailId
        }

        let responseData = (await this.patientTemplateService.find(params)).data.item;
        const groupedByPatientDetail = _.groupBy(responseData, t => t.nikoPatientDetail.id);
        let nikoPatientDetails = Object.values(groupedByPatientDetail).map(patientTemplates => ({
            ...patientTemplates[0].nikoPatientDetail,
            nikoPatient: { ...patientTemplates[0].nikoPatientDetail.nikoPatient, dateOfBirth: toUTCDate(patientTemplates[0].nikoPatientDetail.nikoPatient.dateOfBirth) },
            disabled: false,
            templatesVisible: false,
            patientTemplates: patientTemplates.map(t => ({
                ...t,
                nikoPatientDetail: undefined,
                disabled: false,
            }))
        }));

        nikoPatientDetails.forEach(nikoPatientDetail => {
            nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote;
            this.setHasHistory(nikoPatientDetail);
            this.setDefaultTemplatesVisibility(nikoPatientDetail);
            this.getPatientStatusButtonClassName(nikoPatientDetail);
            this.getSelectedStatus("insurance", nikoPatientDetail);
            nikoPatientDetail.patientTemplates.forEach(template => {
                template.tempNote = template.note;
                template.isAddingNote = false;
                this.getSelectedStatus("intake", null, template);
            });
        });

        index = this.nikoPatientDetails.findIndex(x => x.id == nikoPatientDetailId);
        if (index == -1)
            return;

        if (nikoPatientDetails && nikoPatientDetails[0]) {
            runInAction(() => {
                Object.assign(this.nikoPatientDetails[index], nikoPatientDetails[0]);
            });
        }
        else {
            this.nikoPatientDetails.splice(index, 1);
        }

        this.shouldAutoNavigateToDischargesTab();
    }

    @action.bound
    setHasHistory(nikoPatientDetail) {
        this.createInsuranceCompareModel(nikoPatientDetail);
        nikoPatientDetail.hasHistory = !nikoPatientDetail.isNew &&
            (
                (nikoPatientDetail.primaryActive.insuranceId.current != nikoPatientDetail.primaryActive.insuranceId.initial || nikoPatientDetail.primaryActive.policyNo.current != nikoPatientDetail.primaryActive.policyNo.initial) ||
                (nikoPatientDetail.secondaryActive.insuranceId.current != nikoPatientDetail.secondaryActive.insuranceId.initial || nikoPatientDetail.secondaryActive.policyNo.current != nikoPatientDetail.secondaryActive.policyNo.initial) ||
                (nikoPatientDetail.primaryInactive.insuranceId.current && (nikoPatientDetail.primaryInactive.insuranceId.current != nikoPatientDetail.primaryInactive.insuranceId.initial || nikoPatientDetail.primaryInactive.policyNo.current != nikoPatientDetail.primaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.secondaryInactive.insuranceId.current && (nikoPatientDetail.secondaryInactive.insuranceId.current != nikoPatientDetail.secondaryInactive.insuranceId.initial || nikoPatientDetail.secondaryInactive.policyNo.current != nikoPatientDetail.secondaryInactive.policyNo.initial)) ||
                (nikoPatientDetail.eligibilityStatusId != (nikoPatientDetail.initialNikoPatientDetail ? nikoPatientDetail.initialNikoPatientDetail.EligibilityStatusId : "")) ||
                nikoPatientDetail.patientTemplates.some(pt => !pt.isNew && pt.isPriorAuthNeeded != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded))
            );
    }

    @action.bound
    createInsuranceCompareModel(nikoPatientDetail) {
        nikoPatientDetail.primaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.primaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryActive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };
        nikoPatientDetail.secondaryInactive = {
            id: { initial: null, current: null },
            insuranceId: { initial: null, current: null },
            insurance: { initial: null, current: null },
            policyNo: { initial: null, current: null },
            startDate: { initial: null, current: null },
            endDate: { initial: null, current: null },
        };

        const fill = (list, targetKey) => {
            if (!list) return;
            const map = targetKey === 'current' ?
                { type: 'type', status: 'status', id: 'id', insuranceId: 'insuranceId', insurance: 'insurance', policyNo: 'policyNo', startDate: 'startDate', endDate: 'endDate' }
                :
                { type: 'Type', status: 'Status', id: 'Id', insuranceId: 'InsuranceId', insurance: 'Insurance', policyNo: 'PolicyNo', startDate: 'StartDate', endDate: 'EndDate' };

            list.forEach(i => {
                const key = `${i[map.type].toLowerCase()}${i[map.status]}`;
                const target = nikoPatientDetail[key];
                if (!target) return;
                target.id[targetKey] = i[map.id];
                target.insuranceId[targetKey] = i[map.insuranceId];
                target.insurance[targetKey] = i[map.insurance];
                target.policyNo[targetKey] = i[map.policyNo];
                target.startDate[targetKey] = i[map.startDate];
                target.endDate[targetKey] = i[map.endDate];
            });
        };

        fill(nikoPatientDetail.nikoPatientDetailInsurances, 'current');
        if (nikoPatientDetail.initialNikoPatientDetail) {
            fill(nikoPatientDetail.initialNikoPatientDetail.NikoPatientDetailInsurances, 'initial');
            resolveInitialEligibilityStatus(nikoPatientDetail.initialNikoPatientDetail, this.eligibilityStatuses);
        }
    }

    @action.bound
    getPatientStatusButtonClassName(nikoPatientDetail, status = null) {
        nikoPatientDetail.hospitalClassName = "";
        nikoPatientDetail.dischargedClassName = "";
        nikoPatientDetail.expiredClassName = "";
        nikoPatientDetail.activeClassName = "";

        this.statuses.filter(i => !status || i.id === status).forEach((item) => {
            if (this.selectedStatusAbrv === "in-progress") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active--alt"
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "discharge") {
                if (item.id === "Hospital" && nikoPatientDetail.status === 'Hospital') {
                    nikoPatientDetail.hospitalClassName = "is-active";
                }
                else if (item.id === "Discharged" && nikoPatientDetail.status === 'Discharged') {
                    nikoPatientDetail.dischargedClassName = "is-active";
                }
                else if (item.id === "Expired" && nikoPatientDetail.status === 'Expired') {
                    nikoPatientDetail.expiredClassName = "is-active";
                }
                else if (item.id === "Active" && nikoPatientDetail.status !== 'Hospital' && nikoPatientDetail.status !== 'Discharged' && nikoPatientDetail.status !== 'Expired') {
                    nikoPatientDetail.activeClassName = "is-active";
                }
            }
            else if (this.selectedStatusAbrv === "intake-reviewed" && item.id === "Active") {
                nikoPatientDetail.activeClassName = "is-active";
            }
        });
    }

    @action.bound
    getSelectedStatus(option, nikoPatientDetail, patientTemplate) {
        if (option === "intake") {
            if (patientTemplate.intakeStatusId === this.changeStatusId) {
                patientTemplate.intakeSelectedStatus = "warning-triangle";
            }
            else if (patientTemplate.intakeStatusId === this.noChangeStatusId) {
                patientTemplate.intakeSelectedStatus = "success";
            }
            else if (patientTemplate.intakeStatusId === this.dischargeStatusId) {
                patientTemplate.intakeSelectedStatus = "danger";
            }
        }
        else if (option === "insurance") {
            if (nikoPatientDetail.insuranceStatusId === this.noChangeStatusId) {
                nikoPatientDetail.insuranceSelectedStatus = "success";
            }
            else if (nikoPatientDetail.insuranceStatusId === this.changeStatusId) {
                nikoPatientDetail.insuranceSelectedStatus = "change";
            }
        }
    }

    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderingTypeId}`);
    }

    @action.bound
    orderHistory() {
        window.open(this.rootStore.routerStore.findRoute("master.app.main.niko-facility-order.history").pattern.replace(":id", this.facilityOrder.id));
    }

    @action.bound
    async startInsuranceReviewStatus() {
        await this.setInsuranceReviewStatusStarted();
    }

    @action.bound
    async setInsuranceReviewStatusStarted() {
        if (this.insuranceReviewStatus) {
            return;
        }

        this.disabled = true;
        try {
            const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'insurance-review');
            await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async setInsuranceReviewStatusComplete() {
        if (!this.insuranceReviewStatus || this.insuranceReviewStatus.completedOn) {
            return;
        }

        this.disabled = true;
        try {
            const request = { ...this.insuranceReviewStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
            await this.facilityOrderStatusService.update(request);
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async restartInsuranceReview() {
        if (!this.insuranceReviewStatus || !this.insuranceReviewStatus.completedOn) {
            return;
        }

        this.disabled = true;
        try {
            const request = { ...this.insuranceReviewStatus, completedOn: null, completedByUserId: null };
            await this.facilityOrderStatusService.update(request);
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        finally {
            await this.getFacilityOrderStatus();
            this.disabled = false;
        }
    }

    @action.bound
    async getFacilityOrderStatus() {
        var statuses = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        runInAction(() => {
            this.facilityOrder.facilityOrderStatuses.replace(statuses.data.item);
        });
        this.refreshKey = uuid();
    }

    @action.bound
    async reviewPatientTemplates(nikoPatientDetail, patientTemplateId, isReview) {
        nikoPatientDetail.disabled = true;
        try {
            const updatedItems = _.map(nikoPatientDetail.patientTemplates.filter((pt) => pt.id && (!patientTemplateId || pt.id == patientTemplateId)), (pt) => {
                return {
                    id: pt.id,
                    isReviewed: isReview,
                    pageName: 'ir'
                };
            });
            await this.patientTemplateService.reviewPatientTemplate(updatedItems);
            this.fetchPatientTemplateByPatient(nikoPatientDetail.id);
        }
        catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to review the template.");
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async reviewAllPatientTemplates() {
        this.disabled = true;
        try {
            const patientTemplates = this.nikoPatientDetails.reduce((result, nikoPatientDetail) => {
                result = result.concat(nikoPatientDetail.patientTemplates.filter(obj => obj !== undefined && !obj.isInsuranceReviewed));
                return result;
            }, []);

            if (patientTemplates && patientTemplates.length > 0) {
                const reviewItems = patientTemplates.map(template => ({
                    id: template.id,
                    isReviewed: true,
                    pageName: 'ir'
                }));
                await this.patientTemplateService.reviewPatientTemplate(reviewItems);
            }

        } catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to update the template.", err);
        }
        finally {
            this.queryUtility.fetch();
            this.disabled = false;
        }
    }

    @action.bound
    async checkIsReviewed(setStatus = true) {
        let params = {
            facilityOrderId: this.id,
            intakeStatusIds: `${this.activeNewFlowStatusIds},${this.dischargeStatusId}`,
            isInsuranceReviewed: false,
            pageSize: 1,
            fields: 'id',
        }
        this.isReviewed = !(await this.patientTemplateService.find(params)).data.item.some(x => x.id);

        if (setStatus) {
            this.shouldAutoNavigateToDischargesTab();
        }
    }

    @action.bound
    shouldAutoNavigateToDischargesTab() {
        if (this.selectedStatusAbrv == "in-progress" && !this.isReviewed && this.insuranceReviewStatus && !this.insuranceReviewStatus.completedOn && this.nikoPatientDetails.length == 0) {
            this.setStatus('discharge');
        }
    }

    @action.bound
    showPreviousFlag(nikoPatientDetail) {
        return nikoPatientDetail && nikoPatientDetail.initialNikoPatientDetail && (nikoPatientDetail.status != nikoPatientDetail.initialNikoPatientDetail.Status);
    }

    @action.bound
    changeItemsVisibility(nikoPatientDetail) {
        nikoPatientDetail.templatesVisible = !nikoPatientDetail.templatesVisible;
    }

    @action.bound
    setDefaultTemplatesVisibility(nikoPatientDetail) {
        nikoPatientDetail.templatesVisible = this.selectedStatusAbrv === "in-progress" &&
            !nikoPatientDetail.isNew &&
            nikoPatientDetail.eligibilityStatus && nikoPatientDetail.eligibilityStatus.name === "Eligible" &&
            !(nikoPatientDetail.primaryActive.insurance.current && nikoPatientDetail.primaryActive.insurance.current.name === "Medicare") &&
            nikoPatientDetail.patientTemplates.some(pt => pt.isNew);
    }

    isMasterPatient = (nikoPatientDetail) => {
        return this.facilityOrder && this.facilityOrder.facility
            && this.facilityOrder.facility.nikoMasterPatientId
            && this.facilityOrder.facility.nikoMasterPatientId == nikoPatientDetail.nikoPatientId;
    }

    @action.bound
    async openNikoPatientStatusModal(nikoPatientDetail, status) {
        if (nikoPatientDetail.status === status && !(nikoPatientDetail.hospitalClassName == "is-active--alt" && status == "Hospital")) {
            return;
        }
        this.nikoPatientStatusModal.open({
            status: status,
            nikoPatientDetail: nikoPatientDetail,
            pageName: this.pageName,
            tabName: this.selectedStatusAbrv,
            additionalMessage: this.selectedStatusAbrv == "discharge" && status == 'Active' && 'Patient will be reviewed upon activation. Please confirm that you\'ve applied all necessary updates to the patient\'s insurance.',
            skipValidation: false,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id)
        });
    }

    @action.bound
    async openNikoPatientInsuranceInfoNoteModal(nikoPatientDetail) {
        this.nikoPatientInsuranceInfoNoteModal.open({
            nikoPatientDetail: nikoPatientDetail,
            setTempValue: (nikoPatientDetail) => { nikoPatientDetail.tempInsuranceInfoNote = nikoPatientDetail.insuranceInfoNote }
        });
    }

    @action.bound
    async updateNikoPatientInsuranceInfoNote(nikoPatientDetail, value, save = false) {
        nikoPatientDetail.tempInsuranceInfoNote = value;
        if (save) {
            nikoPatientDetail.insuranceInfoNote = nikoPatientDetail.tempInsuranceInfoNote;
            await this.updateInsuranceInfo(nikoPatientDetail);
        }
    }

    @action.bound
    async updateInsuranceInfo(nikoPatientDetail) {
        nikoPatientDetail.disabled = true;
        try {
            await this.nikoPatientDetailService.updatePatientInsuranceInfo(nikoPatientDetail);
        }
        catch (err) {
            this.rootStore.notificationStore.error('Error occurred while trying to update patient Insurance Information.');
        }
        finally {
            nikoPatientDetail.disabled = false;
        }
    }

    @action.bound
    async openReasonForChangeModal(nikoPatientDetail) {
        this.nikoFacilityOrderReasonForChangeModal.open({
            nikoPatientDetail: nikoPatientDetail,
            getNikoPatient: () => this.fetchPatientTemplateByPatient(nikoPatientDetail.id),
            sendOrder: this.facilityOrder && this.facilityOrder.sendOrder,
            pageName: this.pageName,
        });
    }

    @action.bound
    async openNikoHealthPatient(remoteId) {
        window.open(`${this.rootStore.applicationStore.app.baasic.settings.nikoHealthBaseUrl}v2/#/patients/${remoteId}/demographics`);
    }

    @action.bound
    getChangedFields(nikoPatientDetail) {
        var fields = [];
        var npd = nikoPatientDetail;
        if (!npd.isNew) {
            if (npd.primaryActive.insuranceId.current != npd.primaryActive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryActiveInsuranceId", value: npd.primaryActive.insuranceId.current || null, initialValue: npd.primaryActive.insuranceId.initial || null });
            if (npd.primaryActive.insuranceId.current == npd.primaryActive.insuranceId.initial && npd.primaryActive.policyNo.current != npd.primaryActive.policyNo.initial)
                fields.push({ fieldName: "PrimaryActivePolicyNo", value: npd.primaryActive.policyNo.current || null, initialValue: npd.primaryActive.policyNo.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current != npd.primaryInactive.insuranceId.initial)
                fields.push({ fieldName: "PrimaryInactiveInsuranceId", value: npd.primaryInactive.insuranceId.current || null, initialValue: npd.primaryInactive.insuranceId.initial || null });
            if (npd.primaryInactive.insuranceId.current && npd.primaryInactive.insuranceId.current == npd.primaryInactive.insuranceId.initial && npd.primaryInactive.policyNo.current != npd.primaryInactive.policyNo.initial)
                fields.push({ fieldName: "PrimaryInactivePolicyNo", value: npd.primaryInactive.policyNo.current || null, initialValue: npd.primaryInactive.policyNo.initial || null });
            if (npd.secondaryActive.insuranceId.current != npd.secondaryActive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryActiveInsuranceId", value: npd.secondaryActive.insuranceId.current || null, initialValue: npd.secondaryActive.insuranceId.initial || null });
            if (npd.secondaryActive.insuranceId.current == npd.secondaryActive.insuranceId.initial && npd.secondaryActive.policyNo.current != npd.secondaryActive.policyNo.initial)
                fields.push({ fieldName: "SecondaryActivePolicyNo", value: npd.secondaryActive.policyNo.current || null, initialValue: npd.secondaryActive.policyNo.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current != npd.secondaryInactive.insuranceId.initial)
                fields.push({ fieldName: "SecondaryInactiveInsuranceId", value: npd.secondaryInactive.insuranceId.current || null, initialValue: npd.secondaryInactive.insuranceId.initial || null });
            if (npd.secondaryInactive.insuranceId.current && npd.secondaryInactive.insuranceId.current == npd.secondaryInactive.insuranceId.initial && npd.secondaryInactive.policyNo.current != npd.secondaryInactive.policyNo.initial)
                fields.push({ fieldName: "SecondaryInactivePolicyNo", value: npd.secondaryInactive.policyNo.current || null, initialValue: npd.secondaryInactive.policyNo.initial || null });
            if (npd.eligibilityStatusId != (npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId : null))
                fields.push({ fieldName: "EligibilityStatusId", value: npd.eligibilityStatusId || null, initialValue: npd.initialNikoPatientDetail ? npd.initialNikoPatientDetail.EligibilityStatusId || null : null });
            npd.patientTemplates.forEach(function (pt) {
                if (!pt.isNew && (pt.isPriorAuthNeeded ? true : false) != (pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? true : false))
                    fields.push({ fieldName: "PAR_" + pt.id, value: pt.isPriorAuthNeeded ? "TRUE" : "FALSE", initialValue: pt.initialPatientTemplate && pt.initialPatientTemplate.IsPriorAuthNeeded ? "TRUE" : "FALSE" });
            });
        }
        if (npd.note)
            fields.push({ fieldName: "Note", value: npd.note, initialValue: null });
        return fields;
    }

    @action.bound
    setShowInsuranceChangeHistory(value) {
        this.showInsuranceChangeHistory = value;
    }

    @action.bound
    async fetchInsuranceChangeHistory(nikoPatientDetail) {
        var fields = this.getChangedFields(nikoPatientDetail);
        if (fields.length === 0) return;
        try {
            const response = await this.historyService.getInsuranceChangeHistory(nikoPatientDetail.id, fields);
            const data = response.data || response;
            var historyMap = {};
            if (Array.isArray(data)) {
                data.forEach(function (entry) {
                    historyMap[entry.fieldName] = entry;
                });
            }
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: historyMap });
            });
        } catch (err) {
            runInAction(() => {
                this.insuranceChangeHistoryMap = Object.assign({}, this.insuranceChangeHistoryMap, { [nikoPatientDetail.id]: {} });
            });
        }
    }

    async getFieldHistory(item, fieldName, value) {
        if (item.history && item.history.userName) return;

        if (item.history) {
            const history = (await this.historyService.getRecordHistory({ recordId: item.id, fieldName: fieldName, value: value })).data;
            item.history.userName = history.userName;
            item.history.eventDate = history.eventDate;
        }
    }
}

export default NikoFacilityOrderInsuranceReviewPreviewViewStore;
